import React, { useEffect, useRef, useState } from 'react';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Description as FileIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { uploadBulkFile, getBulkUploadJob, downloadBulkUploadTemplate, type BulkUploadJob } from '../../services/apiService';
import { validateBulkUploadFile } from '../../utils/knowledgeBaseHelpers';

const JOB_POLL_INTERVAL_MS = 2000;

interface BulkUploadModalProps {
  /** Exact draft resolved before this writer opens. */
  draftTaskId: string;
  onClose: () => void;
  /** Fired once the job reaches a terminal state with rows actually added,
   *  so the caller can refetch its own copy of the draft — the upload lands
   *  server-side asynchronously, this modal reporting "done" does not mean
   *  the caller's in-memory suggestions already include the new rows. */
  onJobSettled?: () => void;
}

/**
 * The "add a spreadsheet to this draft" step of onboarding, as a modal
 * instead of a navigation to the standalone /bulk-upload page — an admin
 * mid-review of a seeded draft should never lose that screen just to attach
 * a file. Deliberately a lighter cut of pages/BulkUpload.tsx: the file
 * picker, import-mode choice, template download and this-job's own status
 * are all that matter here. The full job history table on that page is
 * page-level bookkeeping, not part of "add one more source to what I'm
 * reviewing right now" — an admin who wants that can still open Imports &
 * Drafts from the Knowledge Base page.
 */
const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ draftTaskId, onClose, onJobSettled }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'add_missing' | 'replace'>('add_missing');
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<BulkUploadJob | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const jobInProgress = job ? job.status === 'pending' || job.status === 'processing' : false;
  const jobDone = job ? job.status === 'completed' || job.status === 'partial' : false;
  const jobFailed = job?.status === 'failed';

  useEffect(() => {
    const jobId = job?._id;
    if (!jobInProgress || !jobId) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const latest = await getBulkUploadJob(jobId);
        setJob(latest);
      } catch {
        // A missed poll is harmless; the next tick retries.
      }
    }, JOB_POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobInProgress, job?._id]);

  useEffect(() => {
    if (jobDone) onJobSettled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDone, job?._id]);

  const validateAndSetFile = (file: File) => {
    const result = validateBulkUploadFile(file);
    if (!result.isValid) {
      setError(result.errorMessage as string);
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const handleDrag = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') setDragActive(true);
    else if (event.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files?.[0]) validateAndSetFile(event.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      const response = await uploadBulkFile(selectedFile, { draftTaskId, importMode });
      if (response.success && response.data?.job) {
        setJob(response.data.job);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setError(response.error || 'Upload failed.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadBulkUploadTemplate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not download the template.');
    }
  };

  return (
    <div className="apexia-kb modal-backdrop kb-modal-overlay z-[1600] sm:p-6">
      <div role="dialog" aria-modal="true" aria-labelledby="bulk-upload-title" className="modal-panel kb-dialog w-full max-w-xl max-h-full">
        <div className="px-5 py-4 border-b border-hairline flex items-start justify-between gap-4">
          <div>
            <h2 id="bulk-upload-title" className="heading-4 kb-dialog-title">Import a spreadsheet</h2>
            <p className="text-sm text-ink-secondary mt-1">Adds objections and FAQs to this unpublished draft. Nothing goes live until you publish.</p>
          </div>
          <button type="button" onClick={onClose} className="kb-button kb-button-tertiary px-3 py-2 text-sm">Close</button>
        </div>

        <div className="p-5 space-y-4">
          {jobDone && job ? (
            <div className="rounded-xl border border-success bg-success-tint p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                <CheckCircleIcon style={{ fontSize: '1.1rem' }} />
                {job.fileName} processed
              </div>
              <p className="mt-1.5 text-sm text-ink-secondary">
                Added {(job.addedObjections ?? 0) + (job.addedFaqs ?? 0)} of {job.totalObjections + job.totalFaqs} row
                {job.totalObjections + job.totalFaqs === 1 ? '' : 's'} to your draft.
                {job.failedObjections + job.failedFaqs > 0 ? ` ${job.failedObjections + job.failedFaqs} row(s) failed.` : ''}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
                <button type="button" onClick={() => setJob(null)} className="kb-button kb-button-secondary px-3 py-2 text-sm">
                  Upload another file
                </button>
                <button type="button" onClick={onClose} className="kb-button kb-button-primary px-3 py-2 text-sm">
                  Done. Back to review
                </button>
              </div>
            </div>
          ) : jobInProgress && job ? (
            <div className="rounded-xl border border-info bg-info-tint p-4 flex items-center gap-3">
              <RefreshIcon className="animate-spin text-info flex-shrink-0" style={{ fontSize: '1.25rem' }} />
              <div>
                <p className="text-sm font-semibold text-ink">Processing {job.fileName}…</p>
                <p className="text-xs text-ink-secondary mt-0.5">You can close this. The draft will have these rows once it finishes.</p>
              </div>
            </div>
          ) : (
            <>
              {jobFailed && job && (
                <p className="text-sm text-error rounded-lg bg-error-tint border border-error px-3 py-2">
                  {job.fileName} could not be processed. Fix the file and try again.
                </p>
              )}
              <fieldset>
                <legend className="text-sm font-medium text-ink mb-1.5">How should this update the draft?</legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className={`rounded-lg border p-2.5 cursor-pointer text-sm ${importMode === 'add_missing' ? 'border-signature bg-signature-tint' : 'border-hairline'}`}>
                    <span className="flex items-start gap-2">
                      <input type="radio" name="bulkImportMode" checked={importMode === 'add_missing'} onChange={() => setImportMode('add_missing')} className="mt-1" />
                      <span>
                        <span className="block font-semibold text-ink">Add missing only</span>
                        <span className="block text-xs text-ink-secondary mt-0.5">Recommended. Leaves existing draft entries unchanged.</span>
                      </span>
                    </span>
                  </label>
                  <label className={`rounded-lg border p-2.5 cursor-pointer text-sm ${importMode === 'replace' ? 'border-error bg-error-tint' : 'border-hairline'}`}>
                    <span className="flex items-start gap-2">
                      <input type="radio" name="bulkImportMode" checked={importMode === 'replace'} onChange={() => setImportMode('replace')} className="mt-1" />
                      <span>
                        <span className="block font-semibold text-ink">Replace unpublished entries</span>
                        <span className="block text-xs text-ink-secondary mt-0.5">Removes current unpublished objections/FAQs first. Published content is unaffected.</span>
                      </span>
                    </span>
                  </label>
                </div>
              </fieldset>

              <div
                className={`relative rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
                  dragActive ? 'border-signature bg-signature-tint' : selectedFile ? 'border-success bg-success-tint' : 'border-ink-border hover:border-ink-muted'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(event) => event.target.files?.[0] && validateAndSetFile(event.target.files[0])}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  aria-label="Choose an Excel file to upload"
                />
                {selectedFile ? (
                  <div className="flex flex-col items-center">
                    <FileIcon className="text-success mb-1.5" style={{ fontSize: '2rem' }} />
                    <p className="text-sm font-medium text-ink">{selectedFile.name}</p>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="mt-1.5 text-xs font-medium text-error hover:opacity-80"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <UploadIcon className="text-ink-muted mb-1.5" style={{ fontSize: '2rem' }} />
                    <p className="text-sm font-medium text-ink">Drag and drop your Excel file, or click to browse</p>
                    <p className="text-xs text-ink-muted mt-1">.xlsx or .xls</p>
                  </div>
                )}
              </div>

              <button type="button" onClick={handleDownloadTemplate} className="inline-flex items-center gap-1.5 text-xs font-medium text-signature hover:text-signature-strong">
                <DownloadIcon style={{ fontSize: '1rem' }} />
                Download template
              </button>

              {error && <p className="text-sm text-error rounded-lg bg-error-tint border border-error px-3 py-2">{error}</p>}
            </>
          )}
        </div>

        {!jobDone && !jobInProgress && (
          <div className="px-5 py-4 border-t border-hairline flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button type="button" onClick={onClose} className="kb-button kb-button-secondary px-4 py-2.5 text-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="kb-button kb-button-primary px-4 py-2.5 text-sm"
            >
              {uploading ? 'Uploading…' : 'Add to draft'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUploadModal;
